import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { CompanyCard } from "../components/cards";

const FIELDS = [
  { name: "requirement", label: "What do you need?", placeholder: "e.g. Commercial kitchen equipment for 12 hotel properties", required: true },
  { name: "category", label: "Category", placeholder: "e.g. Hospitality / Manufacturing / Pharma", required: true },
  { name: "specification", label: "Specification", placeholder: "Technical specs, grades, standards…" },
  { name: "quantity", label: "Quantity", placeholder: "e.g. 12 property fit-outs" },
  { name: "budget", label: "Budget", placeholder: "e.g. ₹6 Cr / $500K" },
  { name: "location", label: "Location", placeholder: "e.g. Bangalore / Dubai / Europe" },
  { name: "timeline", label: "Timeline", placeholder: "e.g. 90 days" },
  { name: "supplier_type", label: "Preferred supplier type", placeholder: "e.g. Verified manufacturer" },
];

export default function PostRFQ() {
  const [params] = useSearchParams();
  const [form, setForm] = useState(() => {
    const need = params.get("need");
    const offer = params.get("offer");
    if (need) return { requirement: `I need: ${need} — ` };
    if (offer) return { requirement: `I offer: ${offer} — looking for buyers and partners for ` };
    return {};
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/rfq", form);
      setResult(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    return (
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-16" data-testid="rfq-success">
        <div className="card-surface hairline-gold p-8 text-center">
          <CheckCircle2 size={40} className="text-gold mx-auto mb-4" />
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-100">REQUIREMENT BROADCAST</h1>
          <p className="font-mono text-gold text-sm mt-2">{result.rfq.id}</p>
          <p className="text-slate-400 mt-4 flex items-center justify-center gap-2">
            <Loader2 size={14} className="animate-spin" /> {result.message}
          </p>
          <p className="text-[11px] font-mono text-slate-600 mt-2 uppercase tracking-widest">Status: {result.rfq.status}</p>
        </div>
        {result.initial_matches?.length > 0 && (
          <div className="mt-10">
            <p className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-5">Initial verified matches</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {result.initial_matches.map((c) => <CompanyCard key={c.id} c={c} />)}
            </div>
          </div>
        )}
        <div className="text-center mt-10">
          <Link to="/opportunities" className="btn-outline text-xs" data-testid="rfq-back-btn">BACK TO OPPORTUNITIES</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-14" data-testid="rfq-page">
      <p className="gold-kicker mb-3">B2B RFQ / Requirement System</p>
      <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-100">POST WHAT YOU NEED.</h1>
      <p className="mt-3 text-slate-400">Your requirement is structured by AI and matched against verified businesses in the network.</p>

      <form onSubmit={submit} className="card-surface p-6 md:p-8 mt-8 space-y-5" data-testid="rfq-form">
        {FIELDS.map((f) => (
          <div key={f.name}>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">{f.label}{f.required && <span className="text-gold"> *</span>}</label>
            {f.name === "specification" ? (
              <textarea rows={3} required={f.required} value={form[f.name] || ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                placeholder={f.placeholder} data-testid={`rfq-${f.name}-input`} className="input-dark resize-none" />
            ) : (
              <input required={f.required} value={form[f.name] || ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                placeholder={f.placeholder} data-testid={`rfq-${f.name}-input`} className="input-dark" />
            )}
          </div>
        ))}
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">Attachments</label>
          <div className="border border-dashed border-slate-700 rounded-sm p-6 text-center text-xs text-slate-500" data-testid="rfq-attachments">
            Drag specifications, drawings or BOQs here — or click to upload (available after sign-in)
          </div>
        </div>
        {error && <p className="text-sm text-red-400" data-testid="rfq-error">{error}</p>}
        <button type="submit" disabled={loading} data-testid="rfq-submit-btn" className="btn-gold w-full disabled:opacity-60">
          {loading ? "BROADCASTING…" : "FIND SUPPLIERS WITH AI"}
        </button>
      </form>
    </div>
  );
}
